import React, { useState } from 'react'
import { Typography } from 'antd'
import { LockOutlined, TeamOutlined } from '@ant-design/icons'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import RolesList from './pages/Roles/RolesList'
import UsersList, { USERS_PAGE_SIZE } from './pages/Users/UsersList'
import { useRolesQuery } from './hooks/useRolesQuery'
import { useUsersQuery } from './hooks/useUsersQuery'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { READ_ACTION, PERMISSION_MANAGEMENT_SUBJECT } from './constants'
import { useCanManagePermissions } from './hooks/useCanManagePermissions'
import styles from './AccessControl.module.scss'

const { Text } = Typography

type TabKey = 'users' | 'roles'

export default function AccessControl() {
  const [tab, setTab] = useState<TabKey>('users')
  // Route guard already gates by role; this additionally requires the
  // PermissionManagement/Read ability so access is governed by RBAC data.
  // Manage (a superset of Read) unlocks editing within the page.
  const { allowed, isLoading: abilityLoading } = usePermission(
    READ_ACTION,
    PERMISSION_MANAGEMENT_SUBJECT
  )
  const canManage = useCanManagePermissions()
  const { data: roles = [] } = useRolesQuery()
  // Reuse the Users tab's initial query (same key) for the tab-count badge so
  // React Query dedupes it into one request instead of a separate page=1&limit=1 call.
  const { data: usersPage } = useUsersQuery({ page: 1, limit: USERS_PAGE_SIZE })
  const userCount = usersPage?.total ?? 0

  if (!abilityLoading && !allowed) {
    return (
      <PageWrapper>
        <Page title="Permission Management" breadcrumb={null}>
          <div className={styles.page}>
            <div className={styles.card}>
              <Text className={styles.subtitle}>
                You don&apos;t have permission to view permissions.
              </Text>
            </div>
          </div>
        </Page>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <Page title="Permission Management" breadcrumb={null} maxWidth="1600px">
        <div className={styles.page}>
          <div className={styles.card}>
            <Text className={styles.subtitle}>
              {canManage
                ? 'Manage roles, permissions, and user assignments.'
                : 'View roles, permissions, and user assignments (read-only).'}
            </Text>

            <div className={styles.tabs} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'users'}
                className={`${styles.tab} ${tab === 'users' ? styles.tabActive : ''}`}
                onClick={() => setTab('users')}
              >
                <TeamOutlined />
                Users <span className={styles.tabCount}>· {userCount}</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'roles'}
                className={`${styles.tab} ${tab === 'roles' ? styles.tabActive : ''}`}
                onClick={() => setTab('roles')}
              >
                <LockOutlined />
                Roles <span className={styles.tabCount}>· {roles.length}</span>
              </button>
            </div>

            {tab === 'users' ? <UsersList /> : <RolesList />}
          </div>
        </div>
      </Page>
    </PageWrapper>
  )
}
